import Company from '../../types/company';

export interface AppContextInterface {
    actions: AppActions;
    state: AppState;
}

interface AppActions {
    logout: () => void;
    login: (username: string, password: string) => Promise<Response>;
    currentUser: () => Promise<Response>;
    setAuthInfo: (authInfo: AuthInfo) => void;
    searchCompanies: (businessIdOrCompanyName: string) => Promise<Response>;
    fetchKeyFigures: (businessId: string, amount?: number) => Promise<Response>;
    searchCompanyBasicData: (id: string) => Promise<Response>;
    fetchDecisionMakers: (businessId: string) => Promise<Response>;
    fetchManagementBoard: (businessId?: string) => Promise<Response>;
    getCompanyConcernInformation: (businessId: string) => Promise<Response>;
    initializeCompanyPage: (businessId: string) => void;
    cleanUpCompanyPage: () => void;
    fetchBusinessIdHistory: (businessId: string) => Promise<Response>;
    fetchAnalysisPdf: (
        businessId: string,
        language: string,
        concern: string
    ) => Promise<Response>;
    setIsConcernSelected: (isConcernSelected: boolean) => void;
}

export interface User {
    username: string;
    endUserId?: string;
    firstName?: string;
    lastName?: string;
}

export interface AppState {
    auth: AuthInfo;
    companyPageBasicInfo?: Company;
    companyPageKeyFigures?: StatementKeyFigures[];
    companyPageConcernKeyFigures?: StatementKeyFigures[];
    isConcernSelected: boolean;
}

export interface AuthInfo {
    authenticated: boolean;
    firstName: string;
    endUserId?: string;
}

export interface CompanySearchInfo {
    businessId: string;
    name: string;
    state: string;
    city: string;
    old?: boolean;
    expires?: string;
    statementDate?: string;
}

export interface SelectableCompany extends CompanySearchInfo {
    validationResult?:
        | 'BUSINESS_ID_NOT_VALID'
        | 'ALREADY_ADDED_TO_GROUP'
        | 'COMPANY_NOT_FOUND'
        | undefined;
}

export interface CompanyValidationResult {
    businessId: string;
    companyName: string;
    lastFinancialStatement: string;
    validationResult:
        | 'BUSINESS_ID_NOT_VALID'
        | 'ALREADY_ADDED_TO_GROUP'
        | 'COMPANY_NOT_FOUND'
        | 'OK'
        | undefined;
}

export interface CompanySearchResponse {
    content: CompanySearchInfo[];
}

export enum RiskRatingClass {
    RED = '1_RED',
    YELLOW = '2_YELLOW',
    GREEN = '3_GREEN'
}

export interface BasicKeyFigureInfo {
    businessId: string;
    companyName?: string;
    companyStatus?: string;
    concern?: boolean;
    statementDate?: string;
    netProfit?: number;
    turnover?: number;
    performerRankingPoints?: number;
    riskRatingClass?: RiskRatingClass;
}

export interface StatementKeyFigures {
    businessId: string;
    companyName?: string;
    companyStatus?: string;
    concern: boolean;
    statementDate: string;
    aggregateEquity?: number;
    currentRatio?: number;
    ebit?: number;
    ebitPerTurnover?: number;
    equityRatio?: number;
    netProfit?: number;
    netProfitPerTurnover?: number;
    personnelAverage?: number;
    returnOnInvestmentPercent?: number;
    totalAssets?: number;
    turnover?: number;
    turnoverGrowthPercent?: number;
    performerRankingPoints?: number;
    riskRatingClass?: RiskRatingClass;
    sampleSize?: number;
}

export interface KeyFigureData {
    seriesName: string;
    keyFigures: StatementKeyFigures[];
}

export interface KeyFiguresResponse {
    businessId: string;
    companyName: string;
    companyStatus: string;
    keyFigures: StatementKeyFigures[];
    concernKeyFigures: StatementKeyFigures[];
}

export interface AggregationKeyFigures {
    targetYear: string;
    currentRatio?: number;
    equityRatio?: number;
    netProfit?: number;
    netProfitPerTurnover?: number;
    returnOnInvestmentPercent?: number;
    turnover?: number;
    turnoverGrowthPercent?: number;
    sampleSize?: number;
}

export interface AggregationKeyFigureResponse {
    content: AggregationKeyFigures[];
}

export interface PageResponse<T> {
    content: T[];
    total?: number;
    page?: number;
}

export interface FeedItem {
    id?: string;
    title?: string;
    modified?: string;
    published?: string;
    companyIds?: string[];
    source?: string;
    uri?: string;
    content?: string;
}

export interface Industry {
    value: string;
    label: string;
    children: Industry[];
}

export interface IndustryInfo {
    code: string;
    label: string;
}

export interface IndustryResponse {
    content: Industry[];
}

export interface Group {
    groupId: string;
    groupName: string;
}

export interface GroupWithCategoryName {
    groupId: string;
    groupName: string;
    categoryName: string;
    categoryId: string;
}

export interface GroupInfo {
    group: Group;
    numberOfCompanies: number;
}

export interface AddCategoryResponse {
    categoryId: string;
}

export interface CategoryAndGroupAggregations {
    categoryId: string;
    categoryName: string;
    numberOfGroups: number;
    categoryAggregation?: AggregationKeyFigures;
    groupAggregations: GroupAggregation[];
}

export interface GroupAggregation {
    groupId: string;
    groupName: string;
    numberOfCompanies: number;
    groupAggregation?: AggregationKeyFigures;
}

export interface GroupsByCategories {
    categoryName: string;
    categoryId: string;
    groups: Group[];
}

export interface Category {
    categoryId: string;
    categoryName: string;
}
